import React, {useCallback} from 'react';
import styled from 'styled-components';
import {Form} from 'react-bootstrap';
import {useField} from 'formik';
import {Icon} from 'shared/helpers';
import {Number as NumberField} from 'shared/components/Forms/Number';
import {FieldGroup} from 'components/customer/Product/Specs/HardwareDefaultsRow';

interface InnerDrawerCountProps {
    minimum: number;
    maximum: number;
    // told of every count the customer changes
    onChange?: () => void;
}

// The same drawer_amount the Specs tab edits, in the app's number field so
// typing, limits and errors behave as there; the drawers follow it.
export const InnerDrawerCount = ({
    minimum,
    maximum,
    onChange,
}: InnerDrawerCountProps) => {
    const [{value}, {error}, {setValue}] = useField<number>('drawer_amount');
    const count = parseInt(String(value)) || 0;

    const change = useCallback(
        (next: number) => {
            if (next !== count) onChange?.();
            void setValue(next);
        },
        [count, onChange, setValue]
    );

    const setCount = useCallback(
        (_: string, next: string | number) => change(parseInt(String(next))),
        [change]
    );

    const decrement = useCallback(() => change(count - 1), [count, change]);

    const increment = useCallback(() => change(count + 1), [count, change]);

    return (
        <FieldGroup>
            <Form.Label htmlFor="inner-drawer-count">
                Number of inner drawers<span>*</span>
            </Form.Label>
            <Controls>
                <Step
                    type="button"
                    aria-label="Remove a drawer"
                    data-cy="inner-drawer-decrement"
                    disabled={count <= minimum}
                    onClick={decrement}>
                    <Icon iconName="Button-Minus.svg" color="secondary" />
                </Step>
                <Count>
                    <NumberField
                        id="inner-drawer-count"
                        name="drawer_amount"
                        value={count}
                        min={minimum}
                        max={Number.isFinite(maximum) ? maximum : undefined}
                        noSpinners
                        isInvalid={!!error}
                        selectHandler={setCount}
                        fieldIndex={0}
                    />
                </Count>
                <Step
                    type="button"
                    aria-label="Add a drawer"
                    data-cy="inner-drawer-increment"
                    disabled={count >= maximum}
                    onClick={increment}>
                    <Icon iconName="Button-Plus.svg" color="secondary" />
                </Step>
            </Controls>
        </FieldGroup>
    );
};

const Controls = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

// the 25px the product form gives its other plus buttons
const Step = styled.button`
    padding: 0;
    border: 0;
    background: none;
    line-height: 0;

    img {
        width: 25px;
        cursor: pointer;
    }

    &:disabled {
        opacity: 0.5;

        img {
            cursor: not-allowed;
        }
    }
`;

const Count = styled.div`
    width: 70px;

    input {
        text-align: center;
    }
`;
